import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationService } from 'primeng/api';
import { Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { ComponentBase } from 'src/app/components/core/_infrastructure/classes/componentBase';
import { ExceptionManager } from 'src/app/components/core/_infrastructure/classes/handlers/exceptionManager';
import { AuthService } from 'src/app/components/core/_infrastructure/classes/services/auth.service';
import { NotificationService } from 'src/app/components/core/_infrastructure/classes/services/notification.service';
import { NavMenuService } from '../../../../core/components/navmenu/navmenu.service';
import { EnumCodigosTwTcAcciones } from '../../../../_importacion/solicitudes/types/enum';
import { DocumentosSolicitudCamService } from '../../../solicitudes/documentacion/solicitud-documentacion.service';
import { ProgramaVerificacionViewModel, TwDocumentacionPvCamViewModel } from '../pvcam-datosbasicos.types';
import { ProgramaVerificacionService } from '../pvcam.service';

@Component({
    selector: 'app-pvcam-wrapper-edit',
    templateUrl: './pvcam-wrapper-edit.component.html',
})
export class PvCamWrapperEditComponent extends ComponentBase implements OnInit, OnDestroy {
    @HostListener('window:beforeunload', ['$event'])
    forceCheckUnload(): boolean {
        // Prevenimos la pérdida de datos, si el usuario recarga accidentalmente la página.
        // Lo controlamos y el navegador le requiere confirmación para finalizar la recarga.
        this.purgeNotAttachedFiles();
        return false;
    }
    /**
     * @article: Reactive forms with smart dumb components
     * https://medium.com/angular-athens/reactive-forms-with-smart-dumb-components-48ebc81417c7
     */

    visibilitySpinner = false;
    habilitarGuardar = false;
    habilitarAdmision = false;
    pvCam: ProgramaVerificacionViewModel;
    documentPv: TwDocumentacionPvCamViewModel;
    requerirObservaciones = false;
    filePendingToPurge = '';
    previousFileChanged = '';
    successMessage = 'Programa de Verificación guardado correctamente';
    successMessageDocPv = 'Documentación guardada correctamente';
    loseChangesQuestion = '¿Desea perder los cambios pendientes?';
    listUrl = '/campanias/programasverificacion';

    //activatedRoute$ = this.activatedRoute.paramMap.subscribe(params => {
    //    const id: any = params.get('id');
    //    if (id) {
    //        this.getProgramaVerificacion(id);
    //    }
    //});

    constructor(
        private fb: FormBuilder,
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private pvCamService: ProgramaVerificacionService,
        protected notificationService: NotificationService,
        protected authService: AuthService,
        protected exceptionHandler: ExceptionManager,
        private navMenuService: NavMenuService,
        private confirmationService: ConfirmationService,
        private documentosSolicitudCamService: DocumentosSolicitudCamService,
    ) {
        super(authService, notificationService, exceptionHandler);
    }

    ngOnInit(): void {
        this.activatedRoute.paramMap.subscribe(params => {
            const id: any = params.get('id');
            if (id) {
                this.getProgramaVerificacion(id);
            }
        });
        this.preparePresentation();
    }

    private preparePresentation(): void {
        this.buildForm();
    }

    private buildForm(): void {
        this.form = this.fb.group({
            IdPeriodoEntidadAuditoraCam: [0],
            IdEstadoPv: [0],
            EstadoPv: [null],
            Campania: [null],
            Periodo: [null],
            ComunidadAutonoma: [null],
            FechaPresentacion: [null],
            FechaDevolucion: [null],
            FechaAdmision: [null],
            Observaciones: [null],
            Texto: ['', Validators.required],
            NombreArchivo: ['', Validators.required],
        });
    }

    /**
     * Método que invoca a la API para cargar los datos de un PV a partir de su id.
     * @param id number
     * @return void
     */
    private getProgramaVerificacion(id: number): void {
        this.pvCamService
            .getById(id)
            .pipe(
                tap((a: ProgramaVerificacionViewModel) => {
                    this.pvCam = a;
                    this.pvCamService.pvCam = this.pvCam;
                    this.previousFileChanged = this.pvCam.TwDocumentacionPvCam != null ? this.pvCam.TwDocumentacionPvCam.CodigoStorage : '';
                    this.navMenuService.updateMenuServiceForProgramaVerificacion(a.Id!, a.IdEstadoPv!);
                    this.habilitarGuardar =
                        this.loggedInUser!.getAction(EnumCodigosTwTcAcciones.MENU_DATOS_BASICOS_PROGRAMA_VERIFICACION, this.pvCam.IdEstadoPv).Editable &&
                        this.isEA;
                    this.habilitarAdmision = this.loggedInUser!.getAction(EnumCodigosTwTcAcciones.ADMISION_PV, this.pvCam.IdEstadoPv).Visible;
                }),
                catchError(error => {
                    this.catchServerError(error);
                    this.visibilitySpinner = false;
                    this.form.disable();
                    return of(null);
                }),
            )
            .subscribe();
    }
    /**
     * Se recibe el documento cargado en la ventana de detalle
     */
    onFileUpload(documento: TwDocumentacionPvCamViewModel): void {
        this.documentPv = documento;
        //RDLF: no se puede purgar en este momento ya que da un error de FK_TwDocumentacionPvCam
        //this.purgeNotAttachedFiles();
    }

    /**
     * Guardamos el guid (codigo-storage) del fichero que se ha subido y se inicializa la tabla de Documentacion
     * Puede ocurrir que se cierre la ventana sin haber guardado la documentación.
     * Si detectamos ése caso, se purga el fichero, esto es, se elimina de la BD.
     */
    onPendingFileToPurge(fileToPurge: string): void {
        this.filePendingToPurge = fileToPurge;
        this.pvCam.TwDocumentacionPvCam = new TwDocumentacionPvCamViewModel();
    }

    onRestore(): void {
        this.submitted = true;
        this.requerirObservaciones = true;
        this.addValidator('Observaciones', Validators.required);
        if (!this.form.valid) {
            this.addErrorMessage('Revise los datos del Programa de Verificación');
            //this.visibilitySpinner = false;
            return;
        }
        this.confirmationService.confirm({
            message: 'Va a devolver el Programa de Verificación, ¿está seguro?',
            accept: () => {
                let modelPv = new ProgramaVerificacionViewModel();
                modelPv.Id = this.pvCam.Id;
                modelPv.Observaciones = this.form.value.Observaciones;
                modelPv.IdUsuarioComunidadAutonoma = this.loggedInUser!.Id;

                this.pvCamService.restore(modelPv).subscribe({
                    next: () => {
                        this.addSuccessMessage('Programa de Verificación devuelto correctamente');
                        //this.ngOnInit();
                        this.onBack();
                    },
                    error: error => {
                        this.catchServerError(error);
                    },
                });
            },
        });
    }

    onAccept(): void {
        if (!this.form.valid) {
            this.addErrorMessage('Revise los datos del Programa de Verificación');
            //this.visibilitySpinner = false;
            return;
        }
        this.confirmationService.confirm({
            message: 'Va a aceptar el Programa de Verificación, ¿está seguro?',
            accept: () => {
                let modelPv = new ProgramaVerificacionViewModel();
                modelPv.Id = this.pvCam.Id;
                modelPv.Observaciones = this.form.value.Observaciones;
                modelPv.IdUsuarioComunidadAutonoma = this.loggedInUser!.Id;

                this.pvCamService.accept(modelPv).subscribe({
                    next: () => {
                        this.addSuccessMessage('Programa de Verificación aceptado correctamente');
                        //this.ngOnInit();
                        this.onBack();
                    },
                    error: error => {
                        this.catchServerError(error);
                    },
                });
            },
        });
    }

    onReject(): void {
        this.submitted = true;
        this.requerirObservaciones = true;
        this.addValidator('Observaciones', Validators.required);
        if (!this.form.valid) {
            this.addErrorMessage('Revise los datos del Programa de Verificación');
            //this.visibilitySpinner = false;
            return;
        }
        this.confirmationService.confirm({
            message: 'Va a rechazar el Programa de Verificación, ¿está seguro?',
            accept: () => {
                let modelPv = new ProgramaVerificacionViewModel();
                modelPv.Id = this.pvCam.Id;
                modelPv.Observaciones = this.form.value.Observaciones;
                modelPv.IdUsuarioComunidadAutonoma = this.loggedInUser!.Id;

                this.pvCamService.reject(modelPv).subscribe({
                    next: () => {
                        this.addSuccessMessage('Programa de Verificación rechazado correctamente');
                        //this.ngOnInit();
                        this.onBack();
                    },
                    error: error => {
                        this.catchServerError(error);
                    },
                });
            },
        });
    }

    /**
     * Invocación de la API para salvar los cambios realizados en el PV
     * @return void
     */
    onSave(): void {
        this.submitted = true;
        this.visibilitySpinner = true;
        //Comprobar si se ha cargado el documento
        if (this.documentPv == null && this.pvCam.TwDocumentacionPvCam == null) {
            this.addErrorMessage('No se ha cargado el documento');
            this.visibilitySpinner = false;
            return;
        }
        if (!this.form.valid) {
            this.addErrorMessage('Revise los datos del Programa de Verificación');
            this.visibilitySpinner = false;
            return;
        }

        this.confirmationService.confirm({
            message: 'Va a presentar el Programa de Verificación, ¿está seguro?',
            accept: () => {
                //se inicializa la vriable de documentos a purgar
                this.filePendingToPurge = '';
                //se guarda el PV
                let modelPv = new ProgramaVerificacionViewModel();

                modelPv.Id = this.pvCam.Id;

                if (this.documentPv != null) {
                    this.documentPv.Texto = this.form.value.Texto;
                    modelPv.TwDocumentacionPvCam = this.documentPv;
                } else if (this.pvCam.TwDocumentacionPvCam != null) {
                    modelPv.TwDocumentacionPvCam = this.pvCam.TwDocumentacionPvCam;
                    modelPv.TwDocumentacionPvCam.Texto = this.form.value.Texto;
                }

                modelPv.IdUsuarioEntidadAuditora = this.loggedInUser!.Id;

                this.pvCamService
                    .save(modelPv)
                    .pipe(
                        tap(() => {
                            this.visibilitySpinner = false;
                            this.addSuccessMessage(this.successMessage);
                            //RDLF: ver si hay documentos previos que han cambiado para borrarlo de TwDocFileSystem
                            if (this.previousFileChanged != '' && this.previousFileChanged != modelPv.TwDocumentacionPvCam.CodigoStorage) {
                                this.documentosSolicitudCamService.deleteFile(this.previousFileChanged).subscribe();
                            }
                            this.previousFileChanged = '';
                            this.onBack();
                        }),
                        catchError(error => {
                            this.visibilitySpinner = false;
                            this.catchServerError(error);

                            return of(null);
                        }),
                    )
                    .subscribe();
            },
        });
    }

    onBack(): void {
        this.router.navigate([this.listUrl]);
    }

    get formInvalid(): boolean {
        return !this.form.valid || !this.form.dirty || this.form.pristine;
    }

    /***********************************************************************************************************************************************/
    /* Métodos para controlar que si se cierra accidentalmente la ventana y se han quedado ficheros sin vincular a la documentación, sean borrados */
    /***********************************************************************************************************************************************/

    /**
     * Si el usuario ha subido ficheros, pero no se han guardado en la solicitud, los purgamos de la BD.
     * Sabemos que aquellos "adjuntos" que no tienen Id, no existen en la BD, así que se descartan directamente.
     */
    private purgeNotAttachedFiles(): void {
        if (!this.filePendingToPurge) {
            return;
        }
        this.documentosSolicitudCamService.deleteFile(this.filePendingToPurge).subscribe();
    }

    // Usado en pvcam-routing.module.ts
    async canDeactivate(): Promise<boolean | Observable<boolean>> {
        setTimeout(() => {
            //
        }, 100);
        if (this.filePendingToPurge) {
            const response = await this.toLoseChangesQuestion();
            return response;
        } else {
            return true;
        }
    }

    private async toLoseChangesQuestion(): Promise<boolean> {
        return new Promise(resolve => {
            this.confirmationService.confirm({
                message: this.loseChangesQuestion,
                accept: () => {
                    this.purgeNotAttachedFiles();
                    resolve(true);
                },
                reject: () => {
                    resolve(false);
                },
            });
        });
    }

    ngOnDestroy(): void {
        this.purgeNotAttachedFiles();
    }
}
